/**
 * When a measure of the page is worth what it costs.
 *
 * Reading a size or a position (`getBoundingClientRect`, `clientWidth`) right
 * after a write forces the browser to lay the page out there and then. Once,
 * that is nothing. Once per row of a list, on every page the reader scrolls in,
 * it froze the list for 300 to 600 ms a page — growing with every row already
 * loaded — under the reader's scroll.
 *
 * The decisions live here, away from the DOM, so they can be tested without a
 * browser (`tests/e2e/unit/layout-reads.spec.js`).
 */

/**
 * Whether a body-appended menu or tooltip should position itself now.
 *
 * A closed one has no position worth knowing: opening it re-positions it. It
 * still runs once on the way closed, which is what lowers the z-index Buefy
 * raised for the open state. A menu that shows without being "active" — inline,
 * hoverable, an `always` tooltip — is always positioned.
 *
 * @param {Object} menu
 * @param {boolean} menu.isActive
 * @param {boolean} [menu.inline]
 * @param {boolean} [menu.hoverable]
 * @param {boolean} [menu.always]
 * @param {boolean} [menu.positionedOpen] Whether the last position taken was an open one
 * @returns {{ position: boolean, positionedOpen: boolean }}
 */
export function appendedMenuPosition({ isActive, inline, hoverable, always, positionedOpen }) {
    const visible = Boolean(isActive || inline || hoverable || always);

    return {
        position: visible || Boolean(positionedOpen),
        positionedOpen: visible
    };
}

/**
 * Whether a dropdown can wait for its first click before it is mounted.
 *
 * Only one that opens on a click alone: a hoverable one opens under the
 * pointer, an inline one is always open, and other `triggers` (contextmenu,
 * hover, focus) open on events the placeholder does not listen for.
 *
 * @param {Object} options
 * @param {boolean} [options.hoverable]
 * @param {boolean} [options.inline]
 * @param {string[]} [options.triggers]
 */
export function opensOnFirstClick({ hoverable, inline, triggers }) {
    if (hoverable || inline) {
        return false;
    }

    return !triggers || (triggers.length === 1 && triggers[0] === 'click');
}

/**
 * Whether a batch of DOM changes touched only a table's rows or footer.
 *
 * Rows arriving — a page of infinite scroll, a cell refreshing, the loader in
 * the footer — change nothing about the columns' widths.
 *
 * @param {Array<{target: {closest?: Function}}>} records MutationObserver records
 */
export function onlyTableBodyChanged(records) {
    return Array.isArray(records) && records.length > 0 && records.every(record => Boolean(record.target?.closest?.('tbody, tfoot')));
}
